/**
 * School class detail — everything hanging off one class, on one page.
 *
 * Reached by clicking a row on a school's Classes table. The pieces were
 * modals before, which meant a class's roster and its timetable could not be
 * looked at together, and neither could be linked to.
 *
 * Four tabs, in the order an administrator asks about them: who is in it, what
 * it sits, when each paper is, and what it is studying.
 *
 * **Read-mostly, with one deliberate exception.** Exams are authored by the
 * class — any active member — because thirty people know when the physics test
 * is and a wrong date is corrected within the hour. Membership is the exception:
 * the democratic join flow cannot repair a class whose members have all left, or
 * one nobody will approve, so an administrator can add and remove.
 *
 * Ungated: the admin console shows this whether or not `study_planning` is on
 * for students.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Attribute, AttributeGrid, BackButton, Button, DataTable, Input, Modal, SearchFilterBar,
  Select, TabStrip,
  type ColumnDef, type FilterDef, type SortState,
} from '@shared-ui/components';
import { useSubjectLabel } from '@shared-ui/reference-data';
import { userApi } from '../api/userApi';
import { ExamsTab } from './classTabs/ExamsTab';
import { TeachersTab } from './classTabs/TeachersTab';
import { CoverageTab } from './classTabs/CoverageTab';
import { PlanTab } from './classTabs/PlanTab';
import { CLASS_SUBJECTS, OwnerTag, boardsOf } from './Schools';
import {
  schoolsApi,
  type School, type SchoolClass, type SchoolClassSection, type ClassMember,
} from '../api/schoolsApi';
import { PUBLIC_TENANT_ID } from '../api/tenantsApi';
import './Schools.css';

const TAB_IDS = ['general', 'students', 'teachers', 'exams', 'coverage', 'plan'] as const;
type TabId = typeof TAB_IDS[number];

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'general', label: 'General' },
  { id: 'students', label: 'Students' },
  { id: 'teachers', label: 'Teachers' },
  { id: 'exams', label: 'Exams' },
  { id: 'coverage', label: 'Coverage' },
  { id: 'plan', label: 'Plan' },
];

/** What a class teaches, for every picker that hangs off it. Falls back to the
 *  whole catalogue only when the class has not said — an absent list means
 *  unknown, and offering nothing would be worse than offering too much. */
/**
 * What this class teaches — its own answer, with NO fallback.
 *
 * The fallback was a hardcoded seven-subject list that had drifted from the real
 * catalogue (it was missing hindi and both English subjects), so a class that had
 * recorded nothing was quietly given someone else's idea of its curriculum and
 * could never be planned for English at all. A wrong list is worse than an empty
 * one: an empty one prompts, a wrong one is acted on.
 */
function subjectsOf(cls: SchoolClass): string[] {
  return cls.subjects ?? [];
}

function fmtWhen(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function classLabel(c: SchoolClass): string {
  return `Grade ${c.grade}`;
}

export function SchoolClassDetail() {
  const { schoolId = '', classId = '' } = useParams();
  // In the URL, not in state: every drill-in from here (an exam, a paper) comes
  // BACK here, and a tab held in state meant each of those returns dumped the
  // reader on General — nowhere near what they were working on. An unknown or
  // absent value falls back rather than rendering an empty strip.
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('tab');
  const tab: TabId = TAB_IDS.includes(raw as TabId) ? (raw as TabId) : 'general';
  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    // Replace: switching tabs is not a place a Back press should have to unwind.
    setSearchParams(params, { replace: true });
  };

  const { data: school } = useQuery({
    queryKey: ['school', schoolId],
    queryFn: () => schoolsApi.getSchool(schoolId),
    enabled: !!schoolId,
  });

  const { data: classes } = useQuery({
    queryKey: ['school-classes', schoolId],
    queryFn: () => schoolsApi.listClasses(schoolId),
    enabled: !!schoolId,
  });

  const cls = useMemo(
    () => (classes?.classes ?? []).find(c => c.id === classId),
    [classes, classId],
  );

  if (!cls) {
    return (
      <div className="submissions-page">
        <BackButton to={`/schools?id=${schoolId}`} label="school" />
        <div className="page-header"><h1>Class</h1></div>
        <p className="school-hint">Loading…</p>
      </div>
    );
  }

  return (
    // `submissions-page` + `page-header` + TabStrip is the admin page shell
    // every other page uses. A bespoke wrapper here meant this one had its own
    // padding, its own title size and its own tab styling.
    <div className="submissions-page">
      <BackButton to={`/schools?id=${schoolId}`} label={school?.name ?? 'school'} />

      <div className="page-header">
        <h1>{classLabel(cls)}</h1>
        {/* What a person needs to recognise the class. The id is NOT here — it
            is machine identity, it is already on the General tab for the
            support questions that quote it, and a raw guid in the subtitle
            just crowds out the school and the year. */}
        <p className="school-hint">
          {school?.name} · {cls.academicYear}
          {cls.board ? ` · ${cls.board}` : ''}
          {' · '}
          {cls.tenantId === PUBLIC_TENANT_ID ? 'public directory' : `tenant ${cls.tenantId}`}
        </p>
      </div>

      <TabStrip
        ariaLabel="Class views"
        active={tab}
        onChange={setTab}
        tabs={TABS.map(t => ({ key: t.id, label: t.label }))}
      />

      {/* Sections are part of General, not a tab: "10-A, 10-B" is a FACT ABOUT
          the class, the same kind of thing as its board and its subjects, and
          one short table did not earn a tab of its own. `?tab=sections` links
          land on General, where the list now is. */}
      {tab === 'general' && <GeneralTab cls={cls} school={school} schoolId={schoolId} />}
      {tab === 'students' && <StudentsTab cls={cls} />}
      {/* After Students, because who is in the room is what an assignment acts on. */}
      {tab === 'teachers' && (
        <TeachersTab cls={cls} subjects={subjectsOf(cls)} schoolName={school?.name} />
      )}
      {/* Papers live INSIDE their exam rather than in a tab of their own: with
          three to six exams a year, a separate list was the same rows with the
          container stripped off. Opening an exam is the timetable. */}
      {tab === 'exams' && <ExamsTab cls={cls} />}
      {tab === 'coverage' && <CoverageTab cls={cls} subjects={subjectsOf(cls)} />}
      {tab === 'plan' && <PlanTab cls={cls} subjects={subjectsOf(cls)} />}
    </div>
  );
}

/**
 * The sections of the class — "10-A", "10-B".
 *
 * Shown under General rather than in a tab of its own. A section is a NAME under a
 * class and nothing else: everything curricular — board, subjects, coverage, the
 * revision plan, the shared exam timetable — belongs to the class above it and is
 * read by every section, which is the whole reason this is a separate record and
 * not a field on the class. Which sections exist is therefore a fact about the
 * class, and it belongs beside the other facts about the class rather than one
 * click away.
 */
function SectionsPanel({ cls }: { cls: SchoolClass }) {
  const qc = useQueryClient();
  // `true` = adding a new one; a section = opening that one. One dialog either
  // way, because a section is one field today and the same page is where its
  // teachers will be read and set.
  const [editing, setEditing] = useState<SchoolClassSection | true | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['class-sections', cls.id],
    queryFn: () => schoolsApi.listSections(cls.id),
  });
  const sections = useMemo(() => data?.sections ?? [], [data]);

  const { data: members } = useQuery({
    queryKey: ['class-members', cls.id],
    queryFn: () => schoolsApi.listClassMembers(cls.id),
  });
  /** How many active members sit in each. A section nobody is in is not an error — it is
   *  usually one that was just added — but the count is what tells an administrator
   *  whether a duplicate has split a roster. */
  const headcount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of members?.members ?? []) {
      if (m.status !== 'active' || !m.sectionId) continue;
      counts.set(m.sectionId, (counts.get(m.sectionId) ?? 0) + 1);
    }
    return counts;
  }, [members]);

  const columns: ColumnDef<SchoolClassSection>[] = [
    { key: 'name', header: 'Section', sortable: true, sortValue: x => x.name.toLowerCase(), render: x => x.name },
    {
      key: 'members',
      header: 'Students',
      sortable: true,
      sortValue: x => headcount.get(x.id) ?? 0,
      render: x => String(headcount.get(x.id) ?? 0),
    },
    { key: 'createdAt', header: 'Added', sortable: true, sortValue: x => x.createdAt, render: x => fmtWhen(x.createdAt) },
    {
      key: 'createdBy',
      header: 'Added by',
      sortable: true,
      sortValue: x => (x.createdByName ?? '').toLowerCase(),
      render: x => x.createdByName ?? '—',
    },
  ];

  return (
    <section className="school-section">
      <div className="school-toolbar">
        {/* Labelled, because the tab strip used to name this list and no longer
            does. */}
        <h2 className="school-section-title">Sections</h2>
        <Button variant="primary" size="sm" onClick={() => setEditing(true)}>+ Add section</Button>
      </div>

      <DataTable<SchoolClassSection>
        columns={columns}
        data={sections}
        rowKey={x => x.id}
        loading={isLoading}
        bordered
        onRowClick={x => setEditing(x)}
        emptyMessage="This class has no sections recorded. That is normal for a school with one unlabelled section per grade."
      />

      {editing && (
        <SectionDialog
          cls={cls}
          section={editing === true ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['class-sections', cls.id] });
            // The roster names the section it sits in, so a rename changes what
            // the students table reads.
            qc.invalidateQueries({ queryKey: ['class-members', cls.id] });
          }}
        />
      )}
    </section>
  );
}

/**
 * One section — add it, or open the one that was clicked.
 *
 * A dialog rather than a field above the table, matching Add student: creating
 * a section is a different task from reading the list, and a lone input wedged
 * over the rows read as a filter for them. It holds one field today; a section
 * is also where its teachers belong, and that is what this page grows into
 * rather than a second dialog beside it.
 *
 * Renaming keeps the section's id. It is derived from the first name only so it
 * reads in a log line — the roster names the document, and re-deriving would
 * leave the students behind under the old name.
 */
function SectionDialog({ cls, section, onClose, onSaved }: {
  cls: SchoolClass;
  section?: SchoolClassSection;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(section?.name ?? '');
  const dirty = name.trim() !== '' && name.trim() !== section?.name;

  const save = useMutation({
    mutationFn: () => schoolsApi.saveSection(cls.id, name.trim(), section?.id),
    onSuccess: () => { onSaved(); onClose(); },
  });

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={section ? section.name : `Add a section to ${classLabel(cls)}`}
      size="sm"
    >
      <label className="school-field">
        Name
        <div className="school-row">
          <Input
            value={name}
            autoFocus
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && dirty) save.mutate(); }}
            placeholder="B"
          />
          <Button variant="primary" size="sm" disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : section ? 'Save' : 'Add section'}
          </Button>
        </div>
      </label>
      {save.isError && <p className="school-error">{(save.error as Error).message}</p>}
    </Modal>
  );
}

/**
 * Who is in the class — and the ability to correct it, which is the one write
 * on this page.
 *
 * **The two kinds of class read differently, because they ARE different.**
 * A public class is joined by asking (SC-5), so its page is a roster: who is in,
 * who is waiting, and an add dialog for the case the class cannot admit someone
 * itself. A tenant's class is not a club — a school's grade 10 is its grade-10
 * students — so its page is the whole cohort, the API supplies it, and the only
 * write is which section each sits in. Nobody is "added" to their own grade,
 * which is why the dialog is not offered there.
 *
 * Sections edit behind a button rather than in place. On a cohort of two hundred
 * the always-live dropdown turned every row into a control and made a stray
 * click a silent re-assignment; the roster is read far more often than it is
 * corrected.
 */
function StudentsTab({ cls }: { cls: SchoolClass }) {
  const qc = useQueryClient();
  const tenantOwned = !!cls.tenantId && cls.tenantId !== PUBLIC_TENANT_ID;
  const [adding, setAdding] = useState(false);
  const [editingSections, setEditingSections] = useState(false);
  const [filters, setFilters] = useState<Record<string, string>>({});
  // A cohort reads by name; a roster reads by who needs acting on.
  const [sort, setSort] = useState<SortState | undefined>(
    tenantOwned ? { sortBy: 'name', sortOrder: 'asc' } : { sortBy: 'status', sortOrder: 'asc' },
  );

  const { data, isLoading } = useQuery({
    queryKey: ['class-members', cls.id],
    queryFn: () => schoolsApi.listClassMembers(cls.id),
  });

  const { data: sectionData } = useQuery({
    queryKey: ['class-sections', cls.id],
    queryFn: () => schoolsApi.listSections(cls.id),
  });
  const sections = useMemo(() => sectionData?.sections ?? [], [sectionData]);

  const all = useMemo(() => data?.members ?? [], [data]);

  const edit = useMutation({
    mutationFn: ({ userId, action, sectionId }: {
      userId: string; action: 'add' | 'remove' | 'set-section'; sectionId?: string | null;
    }) => schoolsApi.editClassMember(cls.id, userId, action, sectionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['class-members', cls.id] }),
  });

  /** Status options come from the DATA — a filter for a status nobody has is a
   *  dead end. Sections come from the class, since filtering to a section that exists but
   *  is empty is a question worth being able to ask. */
  const filterDefs = useMemo<FilterDef[]>(() => [
    {
      key: 'status',
      placeholder: 'All statuses',
      options: [...new Set(all.map(m => m.status))].sort()
        // 'none' is the API's word for "no membership row"; an administrator
        // reading the filter wants to hear it as the question they are asking.
        .map(v => ({ value: v, label: v === 'none' ? 'Not in the class' : v })),
    },
    ...(sections.length ? [{
      key: 'sectionId',
      placeholder: 'All sections',
      options: [
        ...sections.map(x => ({ value: x.id, label: x.name })),
        // Not the same as "all": these are members who have not said which room
        // they sit in, and they are exactly who an administrator is looking for.
        { value: 'none', label: 'No section said' },
      ],
    }] : []),
  ], [all, sections]);

  const rows = useMemo(() => {
    const term = (filters.search ?? '').trim().toLowerCase();
    return all.filter(m => {
      if (filters.status && m.status !== filters.status) return false;
      if (filters.sectionId === 'none' ? !!m.sectionId
        : filters.sectionId && m.sectionId !== filters.sectionId) return false;
      if (!term) return true;
      return [m.name, m.userId].some(v => (v ?? '').toLowerCase().includes(term));
    });
  }, [all, filters]);

  // A student of the grade with no membership row. Putting them in a section is
  // what puts them in the class, so that one act does both.
  const notInClass = (m: ClassMember) => m.status === 'none';

  const setSection = (m: ClassMember, sectionId: string) => edit.mutate({
    userId: m.userId,
    action: notInClass(m) ? 'add' : 'set-section',
    sectionId: sectionId || null,
  });

  const columns: ColumnDef<ClassMember>[] = [
    { key: 'name', header: 'Student', sortable: true, sortValue: m => (m.name || m.userId).toLowerCase(), render: m => m.name || m.userId },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      sortValue: m => rank(m.status),
      // 'none' is the absence of a row, not a state anyone put the student in —
      // it reads as a dash, the same as every other unset value on this page.
      render: m => (notInClass(m) ? <span className="school-hint">—</span> : m.status),
    },
    {
      key: 'sectionId',
      header: 'Section',
      sortable: true,
      sortValue: m => (m.sectionName ?? '').toLowerCase(),
      render: m => {
        if (sections.length === 0) return '—';
        if (!editingSections) {
          return m.sectionName ?? <span className="school-hint">—</span>;
        }
        return (
          <Select
            value={m.sectionId ?? ''}
            disabled={edit.isPending}
            aria-label={`Section for ${m.name || m.userId}`}
            onChange={e => setSection(m, e.target.value)}
          >
            {/* No "not said" option: once a class HAS sections, everyone belongs
                to one, and offering the empty choice let an administrator undo an
                assignment into a state nothing else on this page can act on. The
                placeholder still renders for members who arrived before that
                rule, so their row says what is missing rather than showing the
                first section as though it were chosen. */}
            {!m.sectionId && <option value="">— pick a section —</option>}
            {sections.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
          </Select>
        );
      },
    },
    { key: 'joinedAt', header: 'Joined', sortable: true, sortValue: m => m.joinedAt, render: m => (notInClass(m) ? '—' : fmtWhen(m.joinedAt)) },
    {
      key: 'actions',
      header: '',
      render: m => (
        <span className="school-row-actions">
          {notInClass(m) ? null : m.status === 'active' ? (
            <button
              type="button"
              className="school-row-icon school-row-icon--danger"
              disabled={edit.isPending}
              aria-label={`Remove ${m.name || m.userId} from this class`}
              title="Remove from this class — the record stays, so re-adding restores the join date"
              onClick={() => edit.mutate({ userId: m.userId, action: 'remove' })}
            >×</button>
          ) : (
            // A tick, not a cross: admitting someone is the opposite act, and
            // the two must never be one glyph apart in meaning.
            <button
              type="button"
              className="school-row-icon"
              disabled={edit.isPending}
              aria-label={m.status === 'pending'
                ? `Approve ${m.name || m.userId}`
                : `Re-add ${m.name || m.userId}`}
              title={m.status === 'pending' ? 'Approve this request' : 'Put them back in the class'}
              onClick={() => edit.mutate({ userId: m.userId, action: 'add' })}
            >✓</button>
          )}
        </span>
      ),
    },
  ];

  return (
    <section className="school-section">
      <div className="school-toolbar">
        <h2 className="school-section-title">Students</h2>
        {sections.length > 0 && (
          <Button
            variant={editingSections ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setEditingSections(v => !v)}
          >
            {editingSections ? 'Done' : 'Edit sections'}
          </Button>
        )}
        {/* Only a public class is JOINED — a tenant's students are already in
            their own grade, so there is nobody to add. */}
        {!tenantOwned && (
          <Button variant="primary" size="sm" onClick={() => setAdding(true)}>+ Add student</Button>
        )}
      </div>

      <SearchFilterBar
        filters={filterDefs}
        values={filters}
        onChange={setFilters}
        searchPlaceholder={tenantOwned ? 'Search this grade…' : 'Search this class…'}
        hideFetch
      />

      <DataTable<ClassMember>
        columns={columns}
        data={rows}
        rowKey={m => m.userId}
        loading={isLoading}
        bordered
        sort={sort}
        onSortChange={setSort}
        emptyMessage={all.length === 0
          ? (tenantOwned
            ? 'No students are recorded in this grade yet.'
            : 'Nobody has joined this class yet.')
          : 'No students match those filters.'}
      />

      {edit.isError && <p className="school-error">{(edit.error as Error).message}</p>}

      {adding && !tenantOwned && (
        <AddStudentDialog
          cls={cls}
          alreadyIn={new Set(all.filter(m => m.status === 'active').map(m => m.userId))}
          onClose={() => setAdding(false)}
          onAdded={() => qc.invalidateQueries({ queryKey: ['class-members', cls.id] })}
        />
      )}
    </section>
  );
}

/**
 * Find a student and put them in the class.
 *
 * Search by name or email, never by user id: nobody knows a user id, so an
 * id-only field meant looking one up on another page and pasting it back.
 * Runs on submit rather than per keystroke, because that endpoint scans.
 */
function AddStudentDialog({ cls, alreadyIn, onClose, onAdded }: {
  cls: SchoolClass;
  alreadyIn: Set<string>;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');

  const found = useQuery({
    queryKey: ['user-search', query],
    queryFn: () => userApi.getUsers({ search: query, pageSize: 8 }),
    enabled: query.length > 1,
  });

  const add = useMutation({
    mutationFn: (userId: string) => schoolsApi.editClassMember(cls.id, userId, 'add'),
    onSuccess: onAdded,
  });

  return (
    <Modal isOpen onClose={onClose} title={`Add a student to ${classLabel(cls)}`} size="md">
      <div className="school-row">
        <Input
          value={term}
          onChange={e => setTerm(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') setQuery(term.trim()); }}
          placeholder="Name or email"
        />
        <Button variant="primary" size="sm" disabled={term.trim().length < 2}
          onClick={() => setQuery(term.trim())}>Search</Button>
      </div>

      {query.length > 1 && (
        <div className="school-search-results">
          {found.isLoading && <p className="school-hint">Searching…</p>}
          {found.data?.items?.length === 0 && <p className="school-hint">No match for “{query}”.</p>}
          {(found.data?.items ?? []).map(u => (
            <div key={u.id} className="school-row">
              <span>{u.name || u.email || u.id}{u.email && u.name ? ` · ${u.email}` : ''}</span>
              {alreadyIn.has(u.id)
                ? <span className="school-hint">already in this class</span>
                : <Button variant="secondary" size="sm" disabled={add.isPending}
                    onClick={() => add.mutate(u.id)}>Add</Button>}
            </div>
          ))}
        </div>
      )}

      <p className="school-hint">
        Adding here skips the usual request-and-approve — use it when the class cannot admit
        someone itself.
      </p>
      {add.isError && <p className="school-error">{(add.error as Error).message}</p>}
    </Modal>
  );
}

/** Active, then waiting, then everyone no longer in the class. */
function rank(status: string): number {
  return status === 'active' ? 0 : status === 'pending' ? 1 : 2;
}

/**
 * The class itself, on the console's detail-grid pattern: a two-column list of
 * labelled cells, read by default, with Edit / Save Changes in the header. Same
 * shape as the user page, so a form here behaves like a form anywhere else.
 *
 * **Only board and subjects become inputs.** Year and grade are the IDENTITY tuple — the
 * class's id is derived from them, and its sections, members, exams and coverage all point
 * at it — so editing one would not rename this class, it would describe a different one.
 * They stay values with the reason said once.
 */
function GeneralTab({ cls, school, schoolId }: {
  cls: SchoolClass;
  school?: School;
  schoolId: string;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [board, setBoard] = useState(cls.board ?? '');
  const [subjects, setSubjects] = useState<string[]>(cls.subjects ?? []);
  const subjectLabel = useSubjectLabel();

  const offered = school ? boardsOf(school) : [];

  const save = useMutation({
    mutationFn: () => schoolsApi.saveClass(schoolId, {
      // The identity tuple goes back unchanged: the endpoint resolves BY it, so
      // this updates THIS class rather than minting another.
      academicYear: cls.academicYear,
      grade: cls.grade,
      board: board || undefined,
      subjects,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['school-classes', schoolId] });
      setEditing(false);
    },
  });

  const cancel = () => {
    setBoard(cls.board ?? '');
    setSubjects(cls.subjects ?? []);
    setEditing(false);
  };

  return (
    <>
      <section className="school-section">
        <div className="school-detail-head">
          {editing ? (
            <>
              {save.isError && <span className="school-error">{(save.error as Error).message}</span>}
              <Button variant="secondary" size="sm" disabled={save.isPending} onClick={cancel}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={save.isPending}
                onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>Edit</Button>
          )}
        </div>

        <AttributeGrid>
          <Attribute label="School">
            <span className="sui-attr-value">{school?.name ?? '—'}</span>
              </Attribute>
          <Attribute label="Owner">
            <span className="sui-attr-value"><OwnerTag tenantId={cls.tenantId} /></span>
              </Attribute>

          <Attribute label="Academic year">
            <span className="sui-attr-value">{cls.academicYear}</span>
              </Attribute>
          <Attribute label="Grade">
            <span className="sui-attr-value">{cls.grade}</span>
              </Attribute>

          <Attribute label="Board">
            <span className="sui-attr-value">
              {editing ? (
                <Select value={board} onChange={e => setBoard(e.target.value)}>
                  <option value="">
                    {offered.length ? '— pick one —' : '— school has no boards set —'}
                  </option>
                  {offered.map(b => <option key={b} value={b}>{b}</option>)}
                </Select>
              ) : (cls.board || <em className="school-public">not set</em>)}
            </span>
              </Attribute>
          <Attribute label="Subjects taught">
            <span className="sui-attr-value">
              {editing ? (
                <span className="school-boards school-boards--inline">
                  {CLASS_SUBJECTS.map(sub => {
                    const on = subjects.includes(sub);
                    return (
                      <label key={sub} className={on ? 'is-on' : ''}>
                        <input type="checkbox" checked={on}
                          onChange={() => setSubjects(cur =>
                            (on ? cur.filter(x => x !== sub) : [...cur, sub]))} />
                        {subjectLabel(sub)}
                      </label>
                    );
                  })}
                </span>
              ) : (cls.subjects?.length
                ? cls.subjects.map(subjectLabel).join(', ')
                : <em className="school-public">not set</em>)}
            </span>
              </Attribute>
        </AttributeGrid>
      </section>

      <SectionsPanel cls={cls} />
    </>
  );
}
